import React, { useMemo } from 'react';
import { Select, ActionIcon, Tooltip, SelectProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconFolderOpen } from '@tabler/icons-react';
import { AssetBundleItem } from '@/API';
import { AssetBundleExplorerModal } from '../AssetBundleExplorerModal';

export interface AssetBundleFileSelectProps extends Omit<SelectProps, 'data' | 'onChange'> {
  data?: (string | { value: string; label: string })[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  workspaceId: string;
  botProviderId: string;
  bundle: AssetBundleItem;
  allowedExtensions?: string[];
  pickerTitle?: string;
  onBundleUpdated?: (updatedBundle: AssetBundleItem) => void;
}

export function AssetBundleFileSelect({
  data = [],
  value,
  onChange,
  workspaceId,
  botProviderId,
  bundle,
  allowedExtensions,
  pickerTitle,
  onBundleUpdated,
  disabled,
  ...selectProps
}: AssetBundleFileSelectProps) {
  const [pickerOpened, { open: openPicker, close: closePicker }] = useDisclosure(false);

  // Ensure selected value exists in options data
  const selectOptions = useMemo(() => {
    const stringData = data.map((item) => (typeof item === 'string' ? item : item.value));
    if (value && !stringData.includes(value)) {
      return [value, ...data];
    }
    return data;
  }, [data, value]);

  return (
    <>
      <Select
        {...selectProps}
        data={selectOptions}
        value={value ?? null}
        onChange={(val) => onChange?.(val)}
        disabled={disabled}
        clearable
        searchable
        rightSectionPointerEvents="all"
        rightSectionWidth={value && !disabled ? 60 : 36}
        rightSection={
          <Tooltip label="開啟資產包目錄挑選檔案" withArrow position="top">
            <ActionIcon
              variant="subtle"
              size="sm"
              color="blue"
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openPicker();
              }}
              disabled={disabled}
              aria-label="開啟檔案挑選器"
            >
              <IconFolderOpen size={16} />
            </ActionIcon>
          </Tooltip>
        }
      />

      <AssetBundleExplorerModal
        opened={pickerOpened}
        onClose={closePicker}
        workspaceId={workspaceId}
        botProviderId={botProviderId}
        bundle={bundle}
        title={pickerTitle || (typeof selectProps.label === 'string' ? `挑選 ${selectProps.label}` : '挑選資產包檔案')}
        mode="pick"
        allowedExtensions={allowedExtensions}
        selectedFilePath={value}
        onSelectConfirm={(selectedPath) => {
          onChange?.(selectedPath);
        }}
        onBundleUpdated={onBundleUpdated}
      />
    </>
  );
}
